import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, MapPin, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { caseStudies } from '@/data/caseStudies';

/**
 * RelatedCaseStudies — reusable widget that surfaces relevant case studies
 * on service pages. Supply either a `filterType` (commercial | sap) to auto-
 * filter, or explicit `routes` to pick specific studies, or neither to show all.
 *
 * Props:
 *   title (string)        — heading, default "Real projects, real results"
 *   subtitle (string)     — subhead under the title
 *   filterType (string)   — 'commercial' | 'sap' — filters caseStudies by .type
 *   routes (string[])     — explicit list of case-study routes to display
 *   limit (number)        — max number to display, default 3
 *   compact (boolean)     — denser styling when used inside service pages
 */
const RelatedCaseStudies = ({
  title = 'Real projects, real results',
  subtitle = 'See how we deliver MEES improvement plans, SAP calculations and Commercial EPCs in practice.',
  filterType,
  routes,
  limit = 3,
  compact = false,
}) => {
  let items = caseStudies;
  if (routes && routes.length) {
    items = caseStudies.filter((c) => routes.includes(c.route));
  } else if (filterType) {
    items = caseStudies.filter((c) => c.type === filterType);
  }
  items = items.slice(0, limit);

  if (items.length === 0) return null;

  const padY = compact ? 'py-12' : 'py-16';

  return (
    <section className={`${padY} bg-gradient-to-b from-gray-50 to-white`} data-testid="related-case-studies">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{title}</h2>
            {subtitle && <p className="text-base text-gray-600 max-w-2xl mx-auto">{subtitle}</p>}
          </div>

          <div className={`grid grid-cols-1 ${items.length === 1 ? 'md:max-w-md md:mx-auto' : items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
            {items.map((s, idx) => (
              <Link
                key={idx}
                to={s.route}
                data-testid={`related-case-study-${idx}`}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all overflow-hidden flex flex-col border border-gray-100"
              >
                <div className="bg-gradient-to-r from-green-600 to-cyan-500 p-5 text-white">
                  <div className="text-[11px] uppercase tracking-wider opacity-90 mb-1">{s.label}</div>
                  <h3 className="text-base font-bold leading-tight">{s.headline}</h3>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
                    <div>
                      <Building2 className="w-3 h-3 text-green-600 mx-auto mb-0.5" />
                      <div className="font-semibold text-gray-900 text-[11px]">{s.area}</div>
                    </div>
                    <div>
                      <MapPin className="w-3 h-3 text-green-600 mx-auto mb-0.5" />
                      <div className="font-semibold text-gray-900 text-[11px]">{s.location}</div>
                    </div>
                    <div>
                      <TrendingUp className="w-3 h-3 text-green-600 mx-auto mb-0.5" />
                      <div className="font-semibold text-gray-900 text-[11px]">{s.ratingFrom} → {s.ratingTo}</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-3 mb-4 flex-1">{s.summary}</p>
                  <span className="text-sm font-semibold text-green-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read case study <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/case-studies">
              <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-base rounded-full" data-testid="view-all-case-studies-btn">
                View all case studies <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelatedCaseStudies;
